import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { db, storage, auth } from "@/config/firebase";
import { doc, onSnapshot, collection, query, where, getDocs, getDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX, Maximize2, Play, Trophy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import VimeoPlayer from "@vimeo/player";
import { LeaderboardStage } from "@/components/economy/Leaderboard";
import { PlayerAvatar } from "@/components/PlayerAvatar";
import { SpinWheel } from "@/components/challenges/SpinWheel";
import { ChallengeLiveStage } from "@/components/challenges/ChallengeLive";

const CURRENT_SEASON = "2026-27";
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Simple API call helper for Stage
async function apiCall(endpoint) {
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;
  
  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }
  });
  
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

// Foyer background videos from Vimeo (unique, no duplicates)
const FOYER_VIDEOS = [
  "1097142932",
  "1097141819",
  "1097140498",
  "1097140669",
  "1097140592",
  "1097140550",
  "1097035235",
  "1097142822",
  "1097143086",
  "1097145938",
  "1097146107",
  "1097146255",
  "1097146510",
  "1097146360",
  "1097147098",
  "1097148201",
  "1097153669",
  "1097156860",
  "1097142988",
];

// Helper: Get authenticated download URL for Firebase Storage
async function getAuthenticatedMediaUrl(urlOrPath) {
  if (!urlOrPath) return null;
  
  // If it's already a download URL with token, return as-is
  if (urlOrPath.includes('token=') || urlOrPath.includes('alt=media&token=')) {
    return urlOrPath;
  }
  
  // If it's a firebasestorage.googleapis.com URL without token, get the storage path
  if (urlOrPath.includes('firebasestorage.googleapis.com')) {
    try {
      const match = urlOrPath.match(/\/o\/([^?]+)/);
      if (match) {
        const storagePath = decodeURIComponent(match[1]);
        const storageRef = ref(storage, storagePath);
        return await getDownloadURL(storageRef);
      }
    } catch (err) {
      console.error('Failed to get download URL:', err);
      return urlOrPath;
    }
  }
  
  // If it's a gs:// URL, extract path
  if (urlOrPath.startsWith('gs://')) {
    try {
      const path = urlOrPath.replace(/gs:\/\/[^\/]+\//, '');
      const storageRef = ref(storage, path);
      return await getDownloadURL(storageRef);
    } catch (err) {
      console.error('Failed to get download URL from gs://:', err);
      return urlOrPath;
    }
  }
  
  return urlOrPath;
}

// Phase constants - Extended for full game flow
const PHASES = {
  // PHASE A - Predictions
  LOBBY: 'LOBBY',
  IDENTITY_GUESS: 'IDENTITY_GUESS',
  IDENTITY_LOCKED: 'IDENTITY_LOCKED',
  IDENTITY_REVEAL: 'IDENTITY_REVEAL',
  PREDICTION_1_VOTING: 'PREDICTION_1_VOTING',
  PREDICTION_1_REVEAL: 'PREDICTION_1_REVEAL',
  PREDICTION_2_VOTING: 'PREDICTION_2_VOTING',
  PREDICTION_2_REVEAL: 'PREDICTION_2_REVEAL',
  USER_COMPLETE: 'USER_COMPLETE',
  PREDICTIONS_COMPLETE: 'PREDICTIONS_COMPLETE',
  
  // PHASE B - Challenge Games
  CHALLENGE_GAME_REVEAL: 'CHALLENGE_GAME_REVEAL',
  CHALLENGE_PLAYER_DRAW: 'CHALLENGE_PLAYER_DRAW',
  CHALLENGE_BETTING_OPEN: 'CHALLENGE_BETTING_OPEN',
  CHALLENGE_BETTING_LOCKED: 'CHALLENGE_BETTING_LOCKED',
  CHALLENGE_PLAYING: 'CHALLENGE_PLAYING',
  CHALLENGE_RESULT: 'CHALLENGE_RESULT',
  CHALLENGES_COMPLETE: 'CHALLENGES_COMPLETE',
  
  // Final
  SESSION_COMPLETE: 'SESSION_COMPLETE',
};

// Foyer Background Video Component — SINGLE robust player.
// Earlier versions ran TWO Vimeo iframes + a 2s watchdog + constant seeks, which
// overloaded the decoder (freezing) and fought the browser autoplay policy (audio dying).
// This version uses ONE player: play a clip fully, then load the next on the 'ended'
// event. A short black dip hides the swap. Audio starts muted (autoplay-safe) and is
// enabled by the user via the "Ton an" button (synchronous unmute keeps the gesture).
const FOYER_FADE_MS = 450; // black dip while the next clip loads

function FoyerVideoBackground({ foyerCmd }) {
  const [order] = useState(() => [...FOYER_VIDEOS].sort(() => Math.random() - 0.5));
  const idxRef = useRef(0);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const wantAudioRef = useRef(false);
  const lastCmdRef = useRef(0);
  const [dimmed, setDimmed] = useState(false);

  const initialUrl = `https://player.vimeo.com/video/${order[0]}?autoplay=1&muted=1&controls=0&playsinline=1&title=0&byline=0&portrait=0&dnt=1&quality=720p`;

  useEffect(() => {
    if (!iframeRef.current) return;
    const p = new VimeoPlayer(iframeRef.current);
    playerRef.current = p;

    const goTo = async (dir) => {
      idxRef.current = (idxRef.current + dir + order.length) % order.length;
      setDimmed(true);
      try {
        await p.loadVideo(Number(order[idxRef.current]));
        await p.setMuted(!wantAudioRef.current);
        await p.setVolume(wantAudioRef.current ? 1 : 0);
        await p.play();
      } catch (e) { /* ignore */ }
      setTimeout(() => setDimmed(false), FOYER_FADE_MS);
    };
    p._goTo = goTo;

    const onEnded = () => goTo(1);
    try { p.on('ended', onEnded); } catch (e) { /* ignore */ }
    // If the user already enabled sound (sticky activation), unmute the first clip too.
    p.ready().then(() => {
      if (wantAudioRef.current) { p.setMuted(false); p.setVolume(1); }
    }).catch(() => {});

    return () => {
      try { p.off('ended', onEnded); } catch (e) { /* ignore */ }
      playerRef.current = null;
    };
  }, [order]);

  // Host remote control (vor / zurück) via session.foyerCmd = { action, nonce }
  useEffect(() => {
    if (!foyerCmd || !foyerCmd.nonce || foyerCmd.nonce === lastCmdRef.current) return;
    lastCmdRef.current = foyerCmd.nonce;
    const p = playerRef.current;
    if (!p || !p._goTo) return;
    if (foyerCmd.action === 'next') p._goTo(1);
    else if (foyerCmd.action === 'prev') p._goTo(-1);
  }, [foyerCmd]);

  // Manueller "Ton an"-Kick vom Stage-Button: MUSS synchron im Klick-Kontext laufen,
  // sonst blockiert der Browser das Unmute des Cross-Origin-iframes. Kein await ready().
  useEffect(() => {
    const kick = () => {
      wantAudioRef.current = true;
      const p = playerRef.current;
      if (!p) return;
      try {
        p.setMuted(false);
        p.setVolume(1);
        p.getPaused().then(pa => { if (pa) p.play().catch(() => {}); }).catch(() => {});
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('foyer-audio-kick', kick);
    return () => window.removeEventListener('foyer-audio-kick', kick);
  }, []);

  const iframeStyle = {
    width: '120vw',
    height: '120vh',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    border: 'none',
    pointerEvents: 'none',
  };

  return (
    <div className="absolute inset-0 overflow-hidden bg-black" data-testid="foyer-video-background">
      <iframe
        ref={iframeRef}
        src={initialUrl}
        className="absolute inset-0"
        style={iframeStyle}
        allow="autoplay; fullscreen"
        title="Foyer Background Video"
        data-testid="foyer-video-slot-0"
      />
      {/* Dark tint for readability */}
      <div className="absolute inset-0 bg-black/50 z-[1]" />
      {/* Black dip that hides the clip swap */}
      <div
        className="absolute inset-0 bg-black pointer-events-none"
        style={{ opacity: dimmed ? 1 : 0, transition: `opacity ${FOYER_FADE_MS}ms ease-in-out`, zIndex: 2 }}
      />
    </div>
  );
}

export default function PredictionsStage() {
  const { sessionId } = useParams();
  
  // Data
  const [session, setSession] = useState(null);
  const [presentations, setPresentations] = useState({});
  const [predictions, setPredictions] = useState([]);
  const [users, setUsers] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [guesses, setGuesses] = useState([]);
  const [votes, setVotes] = useState([]);
  const [leaderboardEntries, setLeaderboardEntries] = useState([]);
  const [seasonSettings, setSeasonSettings] = useState(null);
  
  // Phase B: Challenge State
  const [currentChallengeRound, setCurrentChallengeRound] = useState(null);
  const [challengeBets, setChallengeBets] = useState([]);
  const [eligibleChallengers, setEligibleChallengers] = useState([]);
  const [challengeGames, setChallengeGames] = useState({});
  const [challengeWheelDoneFor, setChallengeWheelDoneFor] = useState(null);
  const [challengerRevealFor, setChallengerRevealFor] = useState(null);
  const [secondaryWheelStep, setSecondaryWheelStep] = useState(0);
  const [secondaryWheelRound, setSecondaryWheelRound] = useState(null);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "challenge_games"), (snap) => {
      const m = {}; snap.docs.forEach(d => { m[d.id] = { id: d.id, ...d.data() }; }); setChallengeGames(m);
    });
    return () => unsub();
  }, []);
  const [wheelSpinning, setWheelSpinning] = useState(false);
  const [wheelTargetIndex, setWheelTargetIndex] = useState(null);
  
  // UI State
  const [countdown, setCountdown] = useState(null);
  const [showIdentityPredictions, setShowIdentityPredictions] = useState(false);
  const [bettingCountdown, setBettingCountdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRevealAnimation, setShowRevealAnimation] = useState(false);

  // Foyer-Einzug (Entrance) + Gewinner-Reveal
  const [entryQueue, setEntryQueue] = useState([]);
  const [entering, setEntering] = useState(null);
  const [currentWagers, setCurrentWagers] = useState([]);
  const [winnersReveal, setWinnersReveal] = useState(null);
  const seenParticipantsRef = useRef(null);
  const usersRef = useRef([]);
  const currentWagersRef = useRef([]);
  const lastResolutionRef = useRef({});
  
  // STAGE ACTIVATION STATE
  const [isStageActivated, setIsStageActivated] = useState(false);
  const [preloadedAssets, setPreloadedAssets] = useState(new Set());
  
  // Audio - synced via Firestore from Host
  const audioRef = useRef(null);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
  
  // Resolved Firebase Storage URLs
  const [resolvedUrls, setResolvedUrls] = useState({});
  
  // Audio loading state
  const [audioLoaded, setAudioLoaded] = useState(false);

  // Load static data + preload assets
  useEffect(() => {
    const loadData = async () => {
      try {
        const [usersSnap, presSnap, predSnap] = await Promise.all([
          getDocs(collection(db, "users")),
          getDocs(query(collection(db, "prediction_presentations"), where("seasonId", "==", CURRENT_SEASON))),
          getDocs(query(collection(db, "season_predictions"), where("seasonId", "==", CURRENT_SEASON))),
        ]);
        
        setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        
        const presData = {};
        presSnap.docs.forEach(d => {
          const data = d.data();
          presData[data.userId] = { id: d.id, ...data };
        });
        setPresentations(presData);
        
        setPredictions(predSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        
        // Preload all assets when stage is activated
        if (isStageActivated) {
          preloadAllAssets(presData);
        }
      } catch (err) {
        console.error("Error loading data:", err);
      }
      setLoading(false);
    };
    
    loadData();
  }, [isStageActivated]);
  
  // Preload all backgrounds and audio with URL resolution
  const preloadAllAssets = useCallback(async (presData) => {
    const newPreloaded = new Set();
    const newResolvedUrls = {};
    
    for (const pres of Object.values(presData)) {
      const userId = pres.userId;
      
      // Resolve and preload background image
      if (pres.backgroundImageUrl) {
        try {
          const resolvedBg = await getAuthenticatedMediaUrl(pres.backgroundImageUrl);
          newResolvedUrls[`${userId}_bg`] = resolvedBg;
          
          const img = new Image();
          img.src = resolvedBg;
          img.onload = () => {
            newPreloaded.add(resolvedBg);
            setPreloadedAssets(prev => new Set([...prev, resolvedBg]));
          };
        } catch (err) {
          console.error('Failed to resolve background URL:', err);
        }
      }
      
      // Resolve and preload audio
      if (pres.songUrl) {
        try {
          const resolvedSong = await getAuthenticatedMediaUrl(pres.songUrl);
          newResolvedUrls[`${userId}_song`] = resolvedSong;
          
          const audio = new Audio();
          audio.src = resolvedSong;
          audio.preload = 'auto';
          audio.oncanplaythrough = () => {
            newPreloaded.add(resolvedSong);
            setPreloadedAssets(prev => new Set([...prev, resolvedSong]));
          };
        } catch (err) {
          console.error('Failed to resolve song URL:', err);
        }
      }
    }
    
    setResolvedUrls(prev => ({ ...prev, ...newResolvedUrls }));
  }, []);

  // Subscribe to session
  useEffect(() => {
    if (!sessionId) return;
    
    const unsub = onSnapshot(
      doc(db, "prediction_sessions", sessionId),
      (snap) => {
        if (snap.exists()) {
          const newSession = { id: snap.id, ...snap.data() };
          
          // Check for phase change to trigger animations
          if (session?.phase !== newSession.phase) {
            if (newSession.phase === PHASES.IDENTITY_REVEAL) {
              setShowRevealAnimation(true);
              setTimeout(() => setShowRevealAnimation(false), 3000);
            }
          }
          
          setSession(newSession);
        }
      },
      (error) => console.error("Session error:", error)
    );
    
    return () => unsub();
  }, [sessionId, session?.phase]);

  // Subscribe to participants
  useEffect(() => {
    if (!sessionId) return;
    
    const unsub = onSnapshot(
      query(collection(db, "prediction_session_participants"), where("sessionId", "==", sessionId)),
      (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setParticipants(list);
        const pid = (p) => p.userId || p.oduid || p.odUserId;
        const currentIds = list.map(pid).filter(Boolean);
        if (seenParticipantsRef.current === null) {
          // Erster Snapshot: bereits Anwesende NICHT animieren
          seenParticipantsRef.current = new Set(currentIds);
        } else {
          const newcomers = currentIds.filter(id => !seenParticipantsRef.current.has(id));
          if (newcomers.length) {
            newcomers.forEach(id => seenParticipantsRef.current.add(id));
            setEntryQueue(prev => [
              ...prev,
              ...newcomers.map(id => {
                const u = usersRef.current.find(x => x.id === id);
                const p = list.find(x => pid(x) === id);
                return {
                  oduid: id,
                  name: u?.display_name || u?.name || p?.displayName || 'Spieler',
                  photoUrl: u?.photo_url || '',
                };
              }),
            ]);
          }
        }
      },
      (error) => console.error("Participants error:", error)
    );
    
    return () => unsub();
  }, [sessionId]);

  // Refs für stabile Lookups in Listener-Closures
  useEffect(() => { usersRef.current = users; }, [users]);
  useEffect(() => { currentWagersRef.current = currentWagers; }, [currentWagers]);

  // Season-Einstellungen (für Foyer-Regelanzeige)
  useEffect(() => {
    const sid = session?.seasonId || "2026-27";
    getDoc(doc(db, "prediction_seasons", sid))
      .then(snap => { if (snap.exists()) setSeasonSettings(snap.data().settings || {}); })
      .catch(() => {});
  }, [session?.seasonId]);

  // Entrance-Queue: nächsten Einzug starten, wenn gerade keiner läuft
  useEffect(() => {
    if (entering || entryQueue.length === 0) return;
    setEntering(entryQueue[0]);
    setEntryQueue(prev => prev.slice(1));
  }, [entryQueue, entering]);

  // Aktuellen Einzug nach kurzer Zeit automatisch ausblenden (Timer wird NICHT durch
  // Queue-Änderungen abgebrochen, nur wenn sich 'entering' selbst ändert).
  useEffect(() => {
    if (!entering) return;
    const t = setTimeout(() => setEntering(null), 3000);
    return () => clearTimeout(t);
  }, [entering]);

  // Wetten der aktuellen Vorhersage (für Gewinner-Reveal)
  useEffect(() => {
    if (!session?.currentPredictionId) { setCurrentWagers([]); return; }
    const unsub = onSnapshot(
      query(collection(db, "prediction_wagers"), where("predictionId", "==", session.currentPredictionId)),
      (snap) => setCurrentWagers(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (error) => console.error("Wagers error:", error)
    );
    return () => unsub();
  }, [session?.currentPredictionId]);

  // Subscribe to guesses for current round
  useEffect(() => {
    if (!session?.currentUserId || !sessionId) {
      setGuesses([]);
      return;
    }
    
    const unsub = onSnapshot(
      query(
        collection(db, "prediction_identity_guesses"),
        where("sessionId", "==", sessionId),
        where("roundUserId", "==", session.currentUserId)
      ),
      (snap) => setGuesses(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (error) => console.error("Guesses error:", error)
    );
    
    return () => unsub();
  }, [session?.currentUserId, sessionId]);

  // Subscribe to votes for current prediction
  useEffect(() => {
    if (!session?.currentPredictionId || !sessionId) {
      setVotes([]);
      return;
    }
    
    const unsub = onSnapshot(
      query(
        collection(db, "prediction_probability_votes"),
        where("sessionId", "==", sessionId),
        where("predictionId", "==", session.currentPredictionId)
      ),
      (snap) => setVotes(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (error) => console.error("Votes error:", error)
    );
    
    return () => unsub();
  }, [session?.currentPredictionId, sessionId]);
  
  // Subscribe to current prediction document (for realtime quote/probability updates)
  useEffect(() => {
    if (!session?.currentPredictionId) return;
    
    const unsub = onSnapshot(
      doc(db, "season_predictions", session.currentPredictionId),
      (snap) => {
        if (snap.exists()) {
          const updatedPred = { id: snap.id, ...snap.data() };
          // Update the predictions array with the latest data
          setPredictions(prev => {
            const idx = prev.findIndex(p => p.id === snap.id);
            if (idx >= 0) {
              const newArr = [...prev];
              newArr[idx] = updatedPred;
              return newArr;
            }
            return prev;
          });
        }
      },
      (error) => console.error("Current prediction error:", error)
    );
    
    return () => unsub();
  }, [session?.currentPredictionId]);

  // Subscribe to leaderboard (for Stage display)
  useEffect(() => {
    const walletsRef = collection(db, "prediction_wallets");
    
    const unsub = onSnapshot(walletsRef, async (snapshot) => {
      const walletData = [];
      
      for (const doc of snapshot.docs) {
        const data = doc.data();
        const balance = data.balance || 0;
        
        if (balance > 0) {
          walletData.push({
            odUserId: doc.id,
            balance: balance,
            reserved: data.reserved || 0,
          });
        }
      }
      
      // Sort by balance DESC
      walletData.sort((a, b) => b.balance - a.balance);
      
      // Enrich with user names (use cached users if available)
      const enriched = walletData.slice(0, 20).map((entry, index) => {
        const user = users.find(u => u.id === entry.odUserId);
        return {
          ...entry,
          userId: entry.odUserId,
          displayName: user?.display_name || user?.name || user?.displayName || user?.username || entry.odUserId.slice(0, 8),
          photoUrl: user?.photo_url || '',
          rank: index + 1,
          available: entry.balance - entry.reserved,
        };
      });
      
      setLeaderboardEntries(enriched);
    });
    
    return () => unsub();
  }, [users]);

  // Predictions (ON/OFF-COURT) erst nach 15s einblenden, damit erst geraten wird
  useEffect(() => {
    if (session?.phase !== PHASES.IDENTITY_GUESS) { setShowIdentityPredictions(false); return; }
    setShowIdentityPredictions(false);
    const t = setTimeout(() => setShowIdentityPredictions(true), 15000);
    return () => clearTimeout(t);
  }, [session?.phase, session?.currentUserId]);

  // Countdown timer (synced from server)
  useEffect(() => {
    if (!session?.phaseEndsAt) {
      setCountdown(null);
      return;
    }
    
    const updateCountdown = () => {
      const endsAt = session.phaseEndsAt.toDate?.() || new Date(session.phaseEndsAt);
      const remaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
      setCountdown(remaining);
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 100);
    
    return () => clearInterval(interval);
  }, [session?.phaseEndsAt]);

  // =============================================
  // PHASE B: CHALLENGE LISTENERS
  // =============================================
  
  // Subscribe to current challenge round
  // Uses Firestore onSnapshot with API fallback on permission errors
  useEffect(() => {
    if (!session?.currentChallengeRoundId) {
      setCurrentChallengeRound(null);
      return;
    }
    
    const roundId = session.currentChallengeRoundId;
    console.log('[Stage] Subscribing to challenge round:', roundId);
    
    let pollInterval = null;
    let usePolling = false;
    let prevPhase = currentChallengeRound?.phase;
    
    // API fallback polling function
    const pollRoundData = async () => {
      try {
        const result = await apiCall(`/api/challenges/round/${roundId}`);
        if (result && result.id) {
          console.log('[Stage] API poll success:', result);
          
          // Detect challenger just drawn - trigger wheel animation
          if (result.phase === 'CHALLENGER_DRAWN' && 
              prevPhase === 'CHALLENGE_DRAWN' && 
              result.challengerUserId) {
            const pool = session?.challengeParticipantIds || session?.activeParticipantIds || [];
            const challenged = new Set(session?.challengedUserIds || []);
            challenged.delete(result.challengerUserId);
            const wheelList = pool.filter(uid => !challenged.has(uid));
            const idx = Math.max(0, wheelList.findIndex(uid => uid === result.challengerUserId));
            setWheelTargetIndex(idx);
            setWheelSpinning(true);
            setTimeout(() => setWheelSpinning(false), 5000);
          }
          prevPhase = result.phase;
          
          setCurrentChallengeRound(result);
        }
      } catch (err) {
        console.error('[Stage] API poll error:', err);
      }
    };
    
    // Try Firestore subscription first
    const unsub = onSnapshot(
      doc(db, "challenge_rounds", roundId),
      (snap) => {
        console.log('[Stage] Challenge round snapshot:', snap.exists(), snap.id);
        if (snap.exists()) {
          const roundData = { id: snap.id, ...snap.data() };
          console.log('[Stage] Challenge round data:', roundData);
          
          // Detect challenger just drawn - trigger wheel animation
          if (roundData.phase === 'CHALLENGER_DRAWN' && 
              prevPhase === 'CHALLENGE_DRAWN' && 
              roundData.challengerUserId) {
            const pool = session?.challengeParticipantIds || session?.activeParticipantIds || [];
            const challenged = new Set(session?.challengedUserIds || []);
            challenged.delete(roundData.challengerUserId);
            const wheelList = pool.filter(uid => !challenged.has(uid));
            const idx = Math.max(0, wheelList.findIndex(uid => uid === roundData.challengerUserId));
            setWheelTargetIndex(idx);
            setWheelSpinning(true);
            setTimeout(() => setWheelSpinning(false), 5000);
          }
          prevPhase = roundData.phase;
          
          setCurrentChallengeRound(roundData);
          
          // Stop polling if we were using it
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        } else {
          console.warn('[Stage] Challenge round doc does not exist');
          pollRoundData();
        }
      },
      (error) => {
        console.error('[Stage] Challenge round subscription error:', error);
        // Switch to API polling on permission errors
        if (!usePolling) {
          usePolling = true;
          console.log('[Stage] Switching to API polling due to Firestore error');
          pollRoundData();
          pollInterval = setInterval(pollRoundData, 2000);
        }
      }
    );
    
    return () => {
      unsub();
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [session?.currentChallengeRoundId, eligibleChallengers]);

  // Subscribe to challenge bets for current round
  useEffect(() => {
    if (!session?.currentChallengeRoundId) {
      setChallengeBets([]);
      return;
    }
    
    const unsub = onSnapshot(
      query(
        collection(db, "challenge_bets"),
        where("roundId", "==", session.currentChallengeRoundId)
      ),
      (snap) => {
        setChallengeBets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    );
    
    return () => unsub();
  }, [session?.currentChallengeRoundId]);

  // Load eligible challengers when entering challenge phase
  useEffect(() => {
    if (!sessionId || !session?.phase?.includes('CHALLENGE')) return;
    
    const loadEligible = async () => {
      try {
        // Get from session data directly
        const challengePool = session.challengeParticipantIds || session.activeParticipantIds || [];
        const alreadyChallenged = new Set(session.challengedUserIds || []);
        // The challenger of the CURRENT round is already added to challengedUserIds by the
        // spin endpoint. Keep them in the wheel so it can visibly land on the winner.
        const currentChallenger = currentChallengeRound?.challengerUserId;
        if (currentChallenger) alreadyChallenged.delete(currentChallenger);
        
        const eligible = challengePool.filter(uid => !alreadyChallenged.has(uid));
        
        // Map to objects with names (prefer session participants, fallback to users)
        const eligibleWithNames = eligible.map(uid => {
          const part = participants.find(p => (p.userId || p.oduid) === uid);
          const user = users.find(u => u.id === uid);
          return {
            userId: uid,
            displayName: part?.displayName || user?.display_name || user?.name || 'Unbekannt',
          };
        });
        
        setEligibleChallengers(eligibleWithNames);
      } catch (err) {
        console.error('Failed to load eligible challengers:', err);
      }
    };
    
    loadEligible();
  }, [sessionId, session?.phase, session?.challengeParticipantIds, session?.challengedUserIds, currentChallengeRound?.challengerUserId, participants, users]);

  // Betting countdown timer
  useEffect(() => {
    if (!currentChallengeRound?.bettingEndsAt) {
      setBettingCountdown(null);
      return;
    }
    
    const updateCountdown = () => {
      const endsAt = currentChallengeRound.bettingEndsAt.toDate?.() || 
                     new Date(currentChallengeRound.bettingEndsAt);
      const remaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
      setBettingCountdown(remaining);
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 100);
    
    return () => clearInterval(interval);
  }, [currentChallengeRound?.bettingEndsAt]);

  // =============================================
  // AUDIO SYNC FROM HOST VIA FIRESTORE
  // Presentation VIDEO (music video with baked-in track) – follows Host audio controls
  const videoRef = useRef(null);
  const [videoVisible, setVideoVisible] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const presVideoUrl = isStageActivated && !videoFailed ? presentations[session?.currentUserId]?.videoUrl : null;

  // Schutz: audioState gilt nur für den User, für den der Host es gesetzt hat (kein Auto-Start mit altem State)
  const hostAudio = session?.audioState;
  const audioForThisUser = !!hostAudio && (!hostAudio.userId || hostAudio.userId === session?.currentUserId);
  const appliedVideoNonce = useRef(null);
  const appliedAudioNonce = useRef(null);
  const playPendingRef = useRef(false);
  useEffect(() => { appliedVideoNonce.current = null; appliedAudioNonce.current = null; playPendingRef.current = false; }, [session?.currentUserId]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !presVideoUrl || !hostAudio) return;
    if (!audioForThisUser) { if (!v.paused) v.pause(); return; }
    v.muted = hostAudio.muted === true;
    v.volume = typeof hostAudio.volume === 'number' && hostAudio.volume > 0 ? Math.min(1, hostAudio.volume) : 1;
    const nonce = hostAudio.restartNonce ?? 0;
    if (appliedVideoNonce.current !== nonce) {
      appliedVideoNonce.current = nonce;
      if (v.currentTime > 0.5) v.currentTime = 0;
    }
    if (hostAudio.playing) {
      // nur EINMAL play() auslösen – verhindert Doppel-Start
      if (v.paused && !playPendingRef.current) {
        playPendingRef.current = true;
        v.play()
          .then(() => { playPendingRef.current = false; })
          .catch(e => { playPendingRef.current = false; console.warn('Video play blocked:', e); });
      }
    } else {
      playPendingRef.current = false;
      if (!v.paused) v.pause();
      if (v.currentTime === 0) setVideoVisible(false);
    }
  }, [presVideoUrl, audioForThisUser, hostAudio?.playing, hostAudio?.muted, hostAudio?.volume, hostAudio?.restartNonce]);
  useEffect(() => { setVideoVisible(false); setVideoFailed(false); }, [session?.currentUserId]);
  useEffect(() => { if (audioRef.current) audioRef.current.muted = !!presVideoUrl || !!(hostAudio?.muted); }, [presVideoUrl, hostAudio?.muted]);

  // Stage-Heartbeat → Host sieht, ob eine aktivierte Stage Ton ausgeben kann
  useEffect(() => {
    if (!sessionId || !isStageActivated) return;
    const beat = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;
        await fetch(`${API_BASE}/api/predictions/session/${sessionId}/stage-heartbeat?activated=true`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) { console.warn('Stage heartbeat failed:', e); }
    };
    beat();
    const iv = setInterval(beat, 10000);
    return () => clearInterval(iv);
  }, [sessionId, isStageActivated]);

  // =============================================
  useEffect(() => {
    if (!hostAudio || !isStageActivated || !audioRef.current || !audioLoaded) return;
    const audio = audioRef.current;
    if (audio.readyState < 3) return;

    // Music video active → song stays silent (video carries the track)
    if (presVideoUrl) {
      if (!audio.paused) audio.pause();
      audio.muted = true;
      return;
    }
    if (!audioForThisUser) { if (!audio.paused) audio.pause(); return; }

    audio.muted = hostAudio.muted === true;
    if (typeof hostAudio.volume === 'number' && Math.abs(hostAudio.volume - audio.volume) > 0.01) audio.volume = hostAudio.volume;

    const nonce = hostAudio.restartNonce ?? 0;
    if (appliedAudioNonce.current !== nonce) {
      appliedAudioNonce.current = nonce;
      if (audio.currentTime > 0.5) audio.currentTime = 0;
    }
    if (hostAudio.playing) {
      if (audio.paused) audio.play().catch(e => console.warn('Audio play blocked:', e));
    } else if (!audio.paused) {
      audio.pause();
    }
  }, [hostAudio, isStageActivated, audioLoaded, presVideoUrl, audioForThisUser]);
  
  // Load audio when user changes (but Host controls playback)
  useEffect(() => {
    const loadAudio = async () => {
      if (!session?.currentUserId || !isStageActivated) return;
      
      const pres = presentations[session.currentUserId];
      if (!pres?.songUrl) return;
      
      // Use resolved URL if available
      let audioUrl = resolvedUrls[`${session.currentUserId}_song`];
      if (!audioUrl) {
        // Resolve URL on demand if not preloaded
        try {
          audioUrl = await getAuthenticatedMediaUrl(pres.songUrl);
          setResolvedUrls(prev => ({ ...prev, [`${session.currentUserId}_song`]: audioUrl }));
        } catch (err) {
          console.error('Failed to resolve audio URL:', err);
          audioUrl = pres.songUrl; // Fallback
        }
      }
      
      if (audioUrl && audioUrl !== currentAudioUrl) {
        setCurrentAudioUrl(audioUrl);
        setAudioLoaded(false);  // Reset loading state
        
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.load();
          
          // Set loaded when audio is ready
          audioRef.current.oncanplaythrough = () => {
            setAudioLoaded(true);
          };
          audioRef.current.onloadeddata = () => {
            setAudioLoaded(true);
          };
          // Don't auto-play - wait for Host sync
        }
      }
    };
    
    loadAudio();
  }, [session?.currentUserId, presentations, currentAudioUrl, isStageActivated, resolvedUrls]);
  
  // =============================================
  // STAGE ACTIVATION (AudioContext unlock)
  // =============================================
  const activateStage = useCallback(async () => {
    // Unlock AudioContext by playing silent audio
    if (audioRef.current) {
      audioRef.current.volume = 0;
      try {
        await audioRef.current.play();
        audioRef.current.pause();
        audioRef.current.volume = typeof session?.audioState?.volume === 'number' ? session.audioState.volume : 0.7;
      } catch (e) {
        console.log('Audio unlock attempt:', e);
      }
    }
    
    // Try fullscreen
    try {
      await document.documentElement.requestFullscreen();
    } catch (e) {
      console.log('Fullscreen not available:', e);
    }
    
    // Preload assets
    if (Object.keys(presentations).length > 0) {
      preloadAllAssets(presentations);
    }
    
    setIsStageActivated(true);
  }, [presentations, preloadAllAssets, session?.audioState?.volume]);

  // Current user data
  const currentUserData = useMemo(() => {
    if (!session?.currentUserId) return null;
    const user = users.find(u => u.id === session.currentUserId);
    const pres = presentations[session.currentUserId];
    const userPreds = predictions.filter(p => p.ownerUserId === session.currentUserId);
    return { user, presentation: pres, predictions: userPreds };
  }, [session?.currentUserId, users, presentations, predictions]);

  // Current prediction
  const currentPrediction = useMemo(() => {
    if (!session?.currentPredictionId) return null;
    return predictions.find(p => p.id === session.currentPredictionId);
  }, [session?.currentPredictionId, predictions]);

  // Gewinner-Reveal: bei Auflösung einer Vorhersage (resolutionStatus MATCHED) die Köpfe der Gewinner zeigen
  useEffect(() => {
    const pred = currentPrediction;
    if (!pred || !pred.id) return;
    const status = pred.resolutionStatus;
    const prev = lastResolutionRef.current[pred.id];
    lastResolutionRef.current[pred.id] = status;
    if (status === 'MATCHED' && prev && prev !== 'MATCHED') {
      // kurze Verzögerung, damit Settlement die WON-Status setzen kann
      const timer = setTimeout(() => {
        const seen = new Set();
        const winners = [];
        currentWagersRef.current
          .filter(w => w.status === 'WON' && w.predictionId === pred.id)
          .forEach(w => {
            if (seen.has(w.userId)) return;
            seen.add(w.userId);
            const u = usersRef.current.find(x => x.id === w.userId);
            winners.push({ name: u?.display_name || u?.name || 'Spieler', photoUrl: u?.photo_url || '' });
          });
        if (winners.length > 0) {
          setWinnersReveal({ text: pred.text, result: pred.result, winners });
          setTimeout(() => setWinnersReveal(null), 8000);
        }
      }, 1800);
      return () => clearTimeout(timer);
    }
  }, [currentPrediction]);

  // Count correct guesses
  const correctGuessCount = useMemo(() => {
    if (!currentUserData?.presentation?.aliases) return 0;
    const aliases = currentUserData.presentation.aliases.map(a => a.toLowerCase().trim());
    return guesses.filter(g => aliases.includes((g.answer || '').toLowerCase().trim())).length;
  }, [guesses, currentUserData]);

  const renderSecondaryPlayers = () => {
              const names = currentChallengeRound.secondaryPlayerNames;
              const step = secondaryWheelRound === currentChallengeRound.id ? secondaryWheelStep : 0;
              if (step < names.length) {
                const poolIds = (session?.challengeParticipantIds?.length ? session.challengeParticipantIds : session?.activeParticipantIds) || [];
                const drawnSoFar = names.slice(0, step);
                const segs = poolIds
                  .map(uid => { const u = users.find(x => x.id === uid); return { id: uid, label: u?.display_name || u?.name || 'Spieler' }; })
                  .filter(sg => sg.id !== currentChallengeRound.challengerUserId && !drawnSoFar.includes(sg.label));
                const targetSeg = segs.find(sg => sg.label === names[step]);
                const segments = targetSeg ? segs : [...segs, { id: `n-${step}`, label: names[step] }];
                return (
                  <div className="mt-6 flex flex-col items-center" data-testid={`secondary-wheel-${step}`}>
                    <SpinWheel
                      key={`${currentChallengeRound.id}-${step}`}
                      title={`RAD ${step + 1} / ${names.length} – MITSPIELER`}
                      testId={`player-wheel-${step}`}
                      segments={segments}
                      targetId={(targetSeg || segments[segments.length - 1]).id}
                      size={Math.min(420, window.innerHeight * 0.45)}
                      duration={3.5}
                      onDone={() => setTimeout(() => { setSecondaryWheelRound(currentChallengeRound.id); setSecondaryWheelStep(step + 1); }, 1200)}
                    />
                    {drawnSoFar.length > 0 && <p className="mt-3 text-xl text-blue-300">{drawnSoFar.join(' • ')}</p>}
                  </div>
                );
              }
              return (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 bg-blue-900/30 border border-blue-500/30 rounded-2xl px-10 py-6 inline-block"
                data-testid="secondary-players-final"
              >
                <p className="text-xl text-blue-400 uppercase mb-3">Mitspieler</p>
                <p className="text-3xl text-white">
                  {currentChallengeRound.secondaryPlayerNames.join(' • ')}
                </p>
              </motion.div>
              );
            };

  // =============================================
  // STAGE ACTIVATION SCREEN
  // =============================================
  if (!isStageActivated) {
    return (
      <div className="w-screen h-screen bg-black flex flex-col items-center justify-center gap-8 select-none" data-testid="stage-gate">
        <div className="absolute top-6 left-6 flex items-center gap-3 px-4 py-2 rounded-full bg-purple-600/20 border border-purple-400/30 text-purple-200 text-sm font-semibold tracking-wide" data-testid="stage-role-badge">
          📺 STAGE · Dieses Fenster gehört auf den BEAMER
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h1 className="text-5xl font-black text-white tracking-wider mb-4">
            RHEIN-STARS PREDICTIONS
          </h1>
          <p className="text-xl text-rose-200/60 mb-8">
            Season 2026/27
          </p>
        </motion.div>
        
        <motion.button
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={activateStage}
          data-testid="activate-stage-btn"
          className="px-12 py-6 bg-gradient-to-r from-rose-600 to-purple-600 text-white text-2xl font-bold rounded-2xl shadow-2xl flex items-center gap-4"
        >
          <Play size={32} />
          STAGE AKTIVIEREN
        </motion.button>
        
        <p className="text-sm text-rose-200/40 max-w-md text-center">
          Klicken um Audio freizugeben und Vollbild zu starten.
          Gesteuert wird NICHT hier, sondern im Steuerungs-Fenster am Laptop.
        </p>
        
        <button
          type="button"
          data-testid="open-host-from-stage"
          onClick={() => window.open(`/admin/predictions/session/${sessionId}`, `rheinstars-host-${sessionId}`)}
          className="text-xs text-rose-200/50 hover:text-rose-100 underline"
        >
          🎮 Steuerung in neuem Fenster öffnen (nur Spielmeister)
        </button>
        
        {session && (
          <div className="mt-4 text-sm text-rose-200/30">
            Session: {session.name} • Phase: {session.phase}
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-screen h-screen bg-black flex items-center justify-center">
        <motion.div 
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-white text-3xl font-light tracking-widest"
        >
          LOADING...
        </motion.div>
      </div>
    );
  }

  const phase = session?.phase || PHASES.LOBBY;
  
  // Use resolved URLs if available, fallback to original
  const currentUserId = currentUserData?.user?.id;
  const backgroundUrl = currentUserId 
    ? (resolvedUrls[`${currentUserId}_bg`] || currentUserData?.presentation?.backgroundImageUrl)
    : currentUserData?.presentation?.backgroundImageUrl;
  const audioState = session?.audioState || {};

  return (
    <div className="w-screen h-screen overflow-hidden bg-black relative select-none">
      {/* Hidden Audio Element - controlled by Host via Firestore */}
      <audio ref={audioRef} loop preload="auto" />

      {/* LEADERBOARD OVERLAY - Controlled by Host */}
      {session?.showLeaderboard && (
        <div className="absolute inset-0 z-[100]">
          <LeaderboardStage entries={leaderboardEntries} title="LEADERBOARD" />
        </div>
      )}

      {/* FOYER-EINZUG: neuer Spieler tritt ein (nur im Foyer/Lobby) */}
      <AnimatePresence>
        {entering && phase === PHASES.LOBBY && (
          <motion.div
            key={`entry-${entering.oduid}`}
            className="absolute inset-0 z-[120] flex flex-col items-center justify-center"
            style={{ background: "radial-gradient(circle at 50% 45%, rgba(16,64,42,0.96) 0%, rgba(6,20,14,0.98) 70%)" }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="foyer-entrance"
          >
            <motion.div
              initial={{ scale: 0.3, y: 120, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ type: "spring", stiffness: 180, damping: 15 }}
              className="flex flex-col items-center"
            >
              <div className="rounded-full ring-8 ring-green-400/50 shadow-[0_0_90px_rgba(74,222,128,0.6)]">
                <PlayerAvatar photoUrl={entering.photoUrl} name={entering.name} size="3xl" />
              </div>
              <p className="mt-10 text-lg uppercase tracking-[0.5em] text-green-300/80">tritt ein</p>
              <h1 className="mt-2 text-6xl font-black text-white text-center px-8" data-testid="foyer-entrance-name">{entering.name}</h1>
              <p className="mt-6 text-2xl">🎉👋</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* GEWINNER-REVEAL: Köpfe der Gewinner bei aufgelöster Vorhersage */}
      <AnimatePresence>
        {winnersReveal && (
          <motion.div
            key="winners-reveal"
            className="absolute inset-0 z-[120] flex flex-col items-center justify-center p-10"
            style={{ background: "radial-gradient(circle at 50% 40%, rgba(120,53,15,0.96) 0%, rgba(20,10,4,0.98) 70%)" }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="winners-reveal"
          >
            <motion.p
              initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
              className="text-lg uppercase tracking-[0.5em] text-amber-300/80"
            >
              Richtig getippt
            </motion.p>
            {winnersReveal.text && (
              <h2 className="mt-2 text-2xl md:text-3xl font-bold text-white/90 text-center max-w-4xl">„{winnersReveal.text}"</h2>
            )}
            <div className="mt-10 flex flex-wrap items-start justify-center gap-8 max-w-6xl">
              {winnersReveal.winners.slice(0, 12).map((w, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.15 + i * 0.12, type: "spring", stiffness: 200, damping: 14 }}
                >
                  <div className="rounded-full ring-4 ring-amber-400/60 shadow-[0_0_50px_rgba(251,191,36,0.5)]">
                    <PlayerAvatar photoUrl={w.photoUrl} name={w.name} size="xl" showName nameClassName="text-lg font-bold text-amber-200 mt-1" />
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
              className="mt-10 text-4xl"
            >
              🏆 Coins kassiert!
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manueller Ton-Rettungs-Button: erzwingt Ton neu (Foyer-Video + Präsentation) */}
      <button
        onClick={() => {
          try { window.dispatchEvent(new Event('foyer-audio-kick')); } catch (e) {}
          const v = videoRef.current;
          if (v && presVideoUrl) { v.muted = false; if (v.paused) v.play().catch(() => {}); }
          const a = audioRef.current;
          if (a && !presVideoUrl && hostAudio?.playing) { a.muted = false; if (a.paused) a.play().catch(() => {}); }
        }}
        className="absolute top-4 right-64 z-50 flex items-center gap-2 bg-green-600/70 px-3 py-2 rounded-full backdrop-blur-sm hover:bg-green-600"
        data-testid="stage-audio-kick-btn"
        title="Ton neu starten, falls er ausgesetzt hat"
      >
        <Volume2 size={18} className="text-white" />
        <span className="text-xs text-white font-semibold">Ton an</span>
      </button>
      {/* Audio Status Indicator (top right corner) */}
      <button
        onClick={() => { if (document.fullscreenElement) { document.exitFullscreen().catch(() => {}); } else { document.documentElement.requestFullscreen().catch(() => {}); } }}
        className="absolute top-4 right-36 z-50 flex items-center gap-2 bg-black/50 px-3 py-2 rounded-full backdrop-blur-sm hover:bg-black/70"
        data-testid="stage-fullscreen-btn"
        title="Vollbild an/aus"
      >
        <Maximize2 size={18} className="text-white/80" />
        <span className="text-xs text-rose-200/60">Vollbild</span>
      </button>
      <div className="absolute top-4 right-4 z-50 flex items-center gap-2 bg-black/50 px-3 py-2 rounded-full backdrop-blur-sm">
        {audioState.muted ? (
          <VolumeX size={18} className="text-rose-400" />
        ) : audioState.playing ? (
          <Volume2 size={18} className="text-green-400 animate-pulse" />
        ) : (
          <Volume2 size={18} className="text-rose-200/40" />
        )}
        <span className="text-xs text-rose-200/60">
          {audioState.playing ? 'AUDIO' : 'PAUSE'}
        </span>
      </div>

      {/* Animated Background Image */}
      <AnimatePresence mode="wait">
        {backgroundUrl && phase !== PHASES.LOBBY && phase !== PHASES.SESSION_COMPLETE && (
          <motion.div
            key={backgroundUrl}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <img 
              src={backgroundUrl} 
              alt="" 
              className="w-full h-full object-cover"
            />
            {/* Cinematic Gradient Overlays */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/60" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
            {/* Vignette */}
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.8) 100%)'
            }} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Music video overlay for the current user's presentation (single element, visibility toggled) */}
      {presVideoUrl && (
        <div className={`absolute inset-0 z-40 bg-black transition-opacity duration-700 ${videoVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-testid="presentation-video-overlay">
          <video ref={videoRef} src={presVideoUrl} className="w-full h-full object-cover" playsInline preload="auto" onPlaying={() => setVideoVisible(true)} onEnded={() => setVideoVisible(false)} onError={() => { console.warn("Presentation video failed → fallback to audio"); setVideoVisible(false); setVideoFailed(true); }} />
        </div>
      )}

      {/* Countdown über dem Video (Bild/Song haben eigenen Ring) → Namenseingabe */}
      {presVideoUrl && videoVisible && phase === PHASES.IDENTITY_GUESS && countdown > 0 && (
        <div className="absolute top-6 left-6 z-50 flex flex-col items-center" data-testid="video-countdown">
          <div className="text-xs uppercase tracking-[0.2em] text-white/90 mb-1 drop-shadow-lg">Name eingeben in</div>
          <div className="w-24 h-24 rounded-full bg-black/70 border-4 border-amber-400 flex items-center justify-center backdrop-blur shadow-[0_0_40px_rgba(251,191,36,0.6)]">
            <span className="text-4xl font-black text-amber-300">{countdown}</span>
          </div>
        </div>
      )}

      {/* Persistent join QR (all phases except LOBBY, which has the big one) */}
      {phase !== PHASES.LOBBY && !session?.showLeaderboard && (
        <div className="absolute bottom-6 right-6 z-30 flex items-center gap-3 bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl p-3" data-testid="persistent-join-qr">
          <div className="bg-white p-1.5 rounded-lg">
            <QRCodeSVG value={`${window.location.origin}/predictions/live/${sessionId}`} size={72} level="M" includeMargin={false} />
          </div>
          <div className="text-left leading-tight">
            <p className="text-[10px] uppercase tracking-widest text-white/50">Beitreten / Wieder rein</p>
            <p className="text-xl font-black text-amber-300 font-mono">{sessionId?.slice(-6).toUpperCase()}</p>
            <p className="text-[10px] text-white/40">App → Predictions → Live</p>
          </div>
        </div>
      )}

      {/* Content based on phase */}
      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center p-8">
        
        {/* ==================== LOBBY ==================== */}
        {phase === PHASES.LOBBY && (
          <>
            {/* Background Video Player */}
            <FoyerVideoBackground foyerCmd={session?.foyerCmd} />
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center relative z-10 w-full max-w-[1600px] mx-auto px-16 h-full flex flex-col justify-between py-3"
            >
              {/* OBEN: Titel + QR/Code (Mitte bleibt frei fürs Video) */}
              <div>
              <motion.h1 
                className="text-5xl font-black text-white mb-2 tracking-tight"
                style={{ textShadow: '0 4px 60px rgba(220, 38, 38, 0.4)' }}
              >
                SEASON PREDICTIONS
              </motion.h1>
              <p className="text-2xl text-white/60 mb-4 font-light">2026/27</p>
              
              <div className="flex items-start justify-between gap-8 w-full">
                {/* QR Code – links außen */}
                <motion.div 
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="bg-white p-5 rounded-3xl shadow-2xl shrink-0"
                >
                  <QRCodeSVG 
                    value={`${window.location.origin}/login?p=123wolle&next=/predictions/live/${sessionId}`}
                    size={170}
                    level="H"
                    includeMargin={false}
                  />
                  <p className="text-black/60 text-sm mt-3 font-medium">Scannen → nur deinen Namen eingeben</p>
                </motion.div>
                
                {/* Session Info – rechts außen */}
                <motion.div 
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="bg-black/40 backdrop-blur-2xl rounded-3xl p-8 border border-white/10 shrink-0"
                >
                  <p className="text-xl text-white/70 mb-4">Session Code</p>
                  <motion.p 
                    className="text-5xl font-mono font-black text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-amber-400"
                    animate={{ scale: [1, 1.02, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {sessionId?.slice(-6).toUpperCase()}
                  </motion.p>
                  <div className="mt-6 flex items-center justify-center gap-3">
                    <motion.span 
                      className="w-3 h-3 rounded-full bg-green-500"
                      animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    />
                    <span className="text-lg text-white/60">
                      {participants.length} Spieler verbunden
                    </span>
                  </div>
                </motion.div>
              </div>
              </div>

              {/* UNTEN: verbundene Spieler + Regeln */}
              <div className="flex flex-col gap-3">
              {/* Verbundene Spieler: Köpfe GANZ UNTEN (nicht in der Mitte), Name darunter */}
              {participants.length > 0 && (
                <div className="order-3 mt-2 flex flex-wrap items-start justify-center gap-x-6 gap-y-3 w-full max-w-[1500px] mx-auto" data-testid="foyer-players">
                  {participants.map((p, idx) => {
                    const pid = p.userId || p.oduid || p.odUserId;
                    const user = users.find(u => u.id === pid);
                    const displayName = user?.display_name || user?.name || p.displayName || 'Spieler';
                    return (
                      <motion.div
                        key={pid || idx}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: Math.min(idx * 0.06, 1.2), type: "spring", stiffness: 200, damping: 16 }}
                      >
                        <PlayerAvatar
                          photoUrl={user?.photo_url}
                          name={displayName}
                          size="lg"
                          showName
                          nameClassName="text-sm font-semibold text-green-300 mt-1 max-w-[110px] truncate"
                        />
                      </motion.div>
                    );
                  })}
                </div>
              )}
              
              <p className="order-1 text-white/30 text-sm">
                Öffne die RheinStars App → Predictions → LIVE BEITRETEN
              </p>

              {/* ==== ÜBERBLICK: SO LÄUFT DER ABEND ==== */}
              <div className="order-2 w-full max-w-[1400px] mx-auto text-left" data-testid="foyer-rules">
                <div className="text-center text-sm uppercase tracking-[0.3em] text-white/40 mb-2">
                  So läuft der Abend · Start: 100 🪙 für alle
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm">
                    <div className="text-xl font-black text-rose-300">1 · Wer ist es?</div>
                    <p className="text-white/70 text-sm mt-1 leading-relaxed"> &amp; Song vorgestellt. Rate, wer sich dahinter verbirgt – für jede <b className="text-white">richtige Antwort gibt's Coins</b>.
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm">
                    <div className="text-xl font-black text-amber-300">2 · Predictions</div>
                    <p className="text-white/70 text-sm mt-1 leading-relaxed">
                      Schätze bei jeder Vorhersage ein, wie wahrscheinlich sie eintritt, und setze Coins. <b className="text-white">Tritt sie ein → Coins nach Quote.</b>
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm">
                    <div className="text-xl font-black text-green-300">3 · Challenges</div>
                    <p className="text-white/70 text-sm mt-1 leading-relaxed">
                      Spieler treten in Duellen an. Wette auf den Ausgang – <b className="text-white">Coins bei richtigem Tipp</b>, Extra-Bonus für die Sieger.
                    </p>
                  </div>
                </div>
                <div className="mt-2 text-center text-white/60 text-base">
                  🏆 Wer am Ende die <b className="text-white">meisten Coins</b> hat, gewinnt die Rangliste!
                </div>
              </div>
              </div>
            </motion.div>
          </>
        )}

        {/* ==================== IDENTITY GUESS ==================== */}
        {(phase === PHASES.IDENTITY_GUESS || phase === PHASES.IDENTITY_LOCKED) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full h-full flex flex-col items-center justify-center"
          >
            {/* Mystery Question */}
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-center mb-8"
            >
              <motion.h2 
                className="text-5xl font-black text-white tracking-widest uppercase"
                style={{ textShadow: '0 0 80px rgba(255,255,255,0.3)' }}
                animate={{ opacity: [0.8, 1, 0.8] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                WER IST DAS?
              </motion.h2>
            </motion.div>
            
            {/* BOTH Predictions as Clues - WITHOUT author name */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="w-full max-w-4xl px-8 space-y-4 mb-8"
            >
              {/* ON-COURT Prediction (erst nach 15s) */}
              {showIdentityPredictions && currentUserData?.predictions?.find(p => p.type === 'on_court') && (
                <motion.div 
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="bg-blue-500/10 backdrop-blur-xl border border-blue-500/30 rounded-2xl p-6"
                >
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">🏀</span>
                    <div>
                      <p className="text-blue-400 text-sm font-bold uppercase tracking-wider mb-2">ON-COURT</p>
                      <p className="text-white text-2xl font-medium leading-relaxed">
                        "{currentUserData.predictions.find(p => p.type === 'on_court')?.text}"
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
              
              {/* OFF-COURT Prediction (erst nach 15s) */}
              {showIdentityPredictions && currentUserData?.predictions?.find(p => p.type === 'off_court') && (
                <motion.div 
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.9 }}
                  className="bg-purple-500/10 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6"
                >
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">🍺</span>
                    <div>
                      <p className="text-purple-400 text-sm font-bold uppercase tracking-wider mb-2">OFF-COURT</p>
                      <p className="text-white text-2xl font-medium leading-relaxed">
                        "{currentUserData.predictions.find(p => p.type === 'off_court')?.text}"
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>

            {/* Countdown Ring – oben links, nicht mittig übers Bild */}
            {countdown !== null && countdown > 0 && !(presVideoUrl && videoVisible) && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 1.1 }}
                className="absolute top-6 left-6 z-50"
              >
                <svg className="w-24 h-24" viewBox="0 0 100 100">
                  <circle
                    cx="50" cy="50" r="45"
                    fill="none"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="4"
                  />
                  <motion.circle
                    cx="50" cy="50" r="45"
                    fill="none"
                    stroke="#dc2626"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={283}
                    strokeDashoffset={283 - (283 * countdown / (session?.settings?.guessSeconds || 30))}
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-5xl font-mono font-black text-white">
                    {countdown}
                  </span>
                </div>
              </motion.div>
            )}

            {/* Guesses count */}
            <motion.div 
              className="mt-6 text-white/50 text-xl"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {guesses.length} Antworten eingegangen
            </motion.div>
          </motion.div>
        )}

        {/* ==================== IDENTITY REVEAL ==================== */}
        {phase === PHASES.IDENTITY_REVEAL && currentUserData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <motion.p
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl text-white/50 mb-6 font-light tracking-widest"
            >
              IT'S...
            </motion.p>
            
            <motion.h1
              initial={{ opacity: 0, scale: 0.3, rotateX: 90 }}
              animate={{ opacity: 1, scale: 1, rotateX: 0 }}
              transition={{ delay: 0.5, duration: 0.8, type: "spring", stiffness: 100 }}
              className="text-8xl font-black text-white uppercase tracking-tight"
              style={{ 
                textShadow: '0 0 100px rgba(220, 38, 38, 0.8), 0 0 200px rgba(220, 38, 38, 0.4)' 
              }}
            >
              {currentUserData.user?.display_name || currentUserData.user?.name}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.5 }}
              className="mt-10"
            >
              <div className="inline-flex items-center gap-4 px-10 py-5 bg-green-500/20 backdrop-blur-xl rounded-2xl border border-green-500/30">
                <span className="text-4xl">🎯</span>
                <span className="text-3xl text-green-400 font-bold">
                  {correctGuessCount} von {guesses.length} richtig
                </span>
                <span className="text-2xl text-green-300">
                  → je +{session?.settings?.correctGuessCoins || 10} Coins
                </span>
              </div>
            </motion.div>

            {/* All guesses */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.0, duration: 0.5 }}
              className="mt-8 mx-auto max-w-6xl"
              data-testid="reveal-all-guesses"
            >
              <p className="text-sm uppercase tracking-[0.3em] text-white/40 mb-4">Alle Antworten</p>
              {guesses.length === 0 ? (
                <p className="text-white/40 text-xl">Niemand hat geraten 😶</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {[...guesses]
                    .map(g => {
                      const aliases = (currentUserData.presentation?.aliases || []).map(a => String(a).toLowerCase().trim());
                      const correct = aliases.includes((g.answer || '').toLowerCase().trim());
                      const voter = users.find(u => u.id === (g.voterUserId || g.oduid));
                      return { ...g, correct, voterName: voter?.display_name || voter?.name || 'Spieler' };
                    })
                    .sort((a, b) => (b.correct - a.correct) || a.voterName.localeCompare(b.voterName, 'de'))
                    .map((g, i) => (
                      <motion.div
                        key={g.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 2.1 + i * 0.08 }}
                        data-testid={`guess-${g.id}`}
                        className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border backdrop-blur-lg text-left ${
                          g.correct ? 'bg-green-500/15 border-green-400/40' : 'bg-white/5 border-white/10'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-xs text-white/50 truncate">{g.voterName}</p>
                          <p className={`text-lg font-semibold truncate ${g.correct ? 'text-green-300' : 'text-white/80'}`}>„{g.answer}“</p>
                        </div>
                        <span className={`text-2xl shrink-0 ${g.correct ? '' : 'opacity-60'}`}>{g.correct ? '✅' : '❌'}</span>
                      </motion.div>
                    ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}

        {/* ==================== PREDICTION VOTING ==================== */}
        {(phase === PHASES.PREDICTION_1_VOTING || phase === PHASES.PREDICTION_2_VOTING ||
          phase === PHASES.PREDICTION_1_LOCKED || phase === PHASES.PREDICTION_2_LOCKED) && currentPrediction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full h-full flex flex-col items-center justify-center px-20"
          >
            {/* Prediction Type Badge */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`px-8 py-3 rounded-full text-2xl font-bold mb-10 ${
                currentPrediction.type === 'on_court' 
                  ? 'bg-blue-500/20 text-blue-300 border-2 border-blue-500/40' 
                  : 'bg-purple-500/20 text-purple-300 border-2 border-purple-500/40'
              }`}
            >
              {currentPrediction.type === 'on_court' ? '🏀 ON COURT' : '🍺 OFF COURT'}
            </motion.div>

            {/* Prediction Text */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-black/60 backdrop-blur-2xl rounded-3xl p-16 max-w-5xl border border-white/10"
            >
              <p className="text-4xl text-white font-medium leading-relaxed text-center">
                "{currentPrediction.text}"
              </p>
              <p className="text-xl text-white/40 mt-8 text-center">
                — {currentPrediction.ownerDisplayName}
              </p>
            </motion.div>

            {/* Countdown & Votes */}
            <div className="mt-16 flex items-center gap-16">
              {countdown !== null && countdown > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="relative"
                >
                  <svg className="w-32 h-32" viewBox="0 0 100 100">
                    <circle
                      cx="50" cy="50" r="45"
                      fill="none"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="4"
                    />
                    <motion.circle
                      cx="50" cy="50" r="45"
                      fill="none"
                      stroke="#f59e0b"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={283}
                      strokeDashoffset={283 - (283 * countdown / (session?.settings?.votingSeconds || 30))}
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-5xl font-mono font-black text-amber-400">
                      {countdown}
                    </span>
                  </div>
                </motion.div>
              )}
              
              <motion.div 
                className="bg-white/10 backdrop-blur-xl rounded-2xl px-10 py-6"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <span className="text-4xl text-white font-light">
                  <span className="font-bold text-amber-400">{votes.length}</span>
                  <span className="text-white/50"> / {participants.filter(p => (p.userId || p.oduid || p.odUserId) !== currentUserData?.user?.id).length} Stimmen</span>
                </span>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* ==================== PREDICTION REVEAL ==================== */}
        {(phase === PHASES.PREDICTION_1_REVEAL || phase === PHASES.PREDICTION_2_REVEAL) && currentPrediction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            {/* Prediction recap */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-black/40 backdrop-blur-xl rounded-2xl p-8 mb-16 max-w-4xl mx-auto"
            >
              <p className="text-2xl text-white/60 line-clamp-2">
                "{currentPrediction.text}"
              </p>
            </motion.div>

            {/* Team Probability */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 100 }}
            >
              <p className="text-3xl text-white/50 mb-4 tracking-widest">TEAM GLAUBT</p>
              <motion.p 
                className="text-[12rem] font-black text-white leading-none"
                style={{ textShadow: '0 0 100px rgba(255,255,255,0.4)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {typeof currentPrediction.teamProbability === 'number'
                  ? `${currentPrediction.teamProbability.toFixed(1)}%`
                  : `${votes.length > 0 ? (votes.reduce((sum, v) => sum + (v.probability || 0), 0) / votes.length).toFixed(1) : '—'}%`
                }
              </motion.p>
            </motion.div>

            {/* Quote */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, type: "spring" }}
              className="mt-16"
            >
              <p className="text-3xl text-amber-400/70 mb-4 tracking-widest">QUOTE</p>
              <motion.div 
                className="inline-block bg-gradient-to-r from-amber-500/10 via-amber-400/20 to-amber-500/10 rounded-3xl px-20 py-10 border-2 border-amber-500/40"
                animate={{ 
                  boxShadow: ['0 0 40px rgba(245, 158, 11, 0.2)', '0 0 80px rgba(245, 158, 11, 0.4)', '0 0 40px rgba(245, 158, 11, 0.2)']
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <span 
                  className="text-[8rem] font-black text-amber-400 leading-none"
                  style={{ textShadow: '0 0 60px rgba(245, 158, 11, 0.6)' }}
                >
                  {typeof currentPrediction.quote === 'number'
                    ? `x${currentPrediction.quote.toFixed(2)}`
                    : votes.length > 0 
                      ? `x${(100 / Math.max(1, votes.reduce((sum, v) => sum + (v.probability || 0), 0) / votes.length)).toFixed(2)}`
                      : 'x—'
                  }
                </span>
              </motion.div>
            </motion.div>

            {/* Votes count */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
              className="text-xl text-white/30 mt-10"
            >
              {currentPrediction.voteCount ?? votes.length} Stimmen
            </motion.p>
          </motion.div>
        )}

        {/* ==================== USER COMPLETE → ZWISCHENSTAND ==================== */}
        {phase === PHASES.USER_COMPLETE && currentUserData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center w-full max-w-5xl mx-auto"
            data-testid="user-complete-standings"
          >
            <div className="flex items-center justify-center gap-4 mb-8">
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
                className="text-5xl"
              >
                ✅
              </motion.span>
              <h2 className="text-4xl font-black text-white">
                {currentUserData.user?.display_name || currentUserData.user?.name} <span className="text-white/40 font-light">fertig</span>
              </h2>
            </div>

            <p className="text-sm uppercase tracking-[0.4em] text-amber-300/80 mb-4">🪙 Zwischenstand Coins</p>
            {leaderboardEntries.length === 0 ? (
              <p className="text-white/40 text-xl">Noch keine Coins vergeben</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {leaderboardEntries.slice(0, 12).map((e, i) => (
                  <motion.div
                    key={e.userId}
                    initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.07 }}
                    data-testid={`standing-${e.userId}`}
                    className={`flex items-center justify-between px-5 py-3 rounded-xl border backdrop-blur-lg ${
                      i === 0 ? 'bg-amber-500/20 border-amber-400/50' :
                      i < 3 ? 'bg-white/10 border-white/20' : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-8 text-right font-black ${i === 0 ? 'text-amber-300 text-2xl' : 'text-white/60 text-lg'}`}>
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${e.rank}.`}
                      </span>
                      <PlayerAvatar photoUrl={e.photoUrl} name={e.displayName} size="sm" />
                      <span className="text-xl font-semibold text-white truncate">{e.displayName}</span>
                    </div>
                    <span className={`text-2xl font-black tabular-nums ${i === 0 ? 'text-amber-300' : 'text-white'}`}>{e.balance}</span>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ==================== PREDICTIONS COMPLETE (Phase A Done) ==================== */}
        {phase === PHASES.PREDICTIONS_COMPLETE && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ duration: 3, repeat: Infinity }}
              className="text-[10rem] mb-10"
            >
              🎯
            </motion.div>
            <motion.h1 
              className="text-8xl font-black text-white mb-8"
              style={{ textShadow: '0 0 60px rgba(34, 197, 94, 0.5)' }}
            >
              PHASE A COMPLETE!
            </motion.h1>
            <p className="text-3xl text-white/50 mb-8">
              Alle Predictions haben jetzt ihre Quote!
            </p>
            <motion.p 
              className="text-2xl text-amber-400"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Challenge Games starten gleich...
            </motion.p>
          </motion.div>
        )}

        {/* ==================== PHASE B: CHALLENGE GAMES ==================== */}
        
        {/* CHALLENGE_LOBBY - Waiting for first challenge */}
        {(phase === 'CHALLENGE_LOBBY' || phase === 'CHALLENGE_SETTLED') && !currentChallengeRound && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <motion.div
              animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-[10rem] mb-10"
            >
              🎮
            </motion.div>
            <h1 className="text-7xl font-black text-white mb-6">
              CHALLENGE GAMES
            </h1>
            <p className="text-3xl text-white/50">
              Die nächste Challenge wird gezogen...
            </p>
          </motion.div>
        )}

        {/* CHALLENGE_DRAWN - Challenge revealed, waiting for challenger */}
        {currentChallengeRound?.phase === 'CHALLENGE_DRAWN' && challengeWheelDoneFor !== currentChallengeRound.id && (
          <div className="text-center" data-testid="challenge-wheel-stage">
            <SpinWheel
              title="🎡 CHALLENGE-RAD"
              testId="challenge-wheel"
              segments={(currentChallengeRound.wheelChallengeIds?.length ? currentChallengeRound.wheelChallengeIds : [currentChallengeRound.challengeId])
                .map(id => ({ id, label: challengeGames[id]?.title || (id === currentChallengeRound.challengeId ? currentChallengeRound.challengeTitle : '…') }))}
              targetId={currentChallengeRound.challengeId}
              size={Math.min(560, window.innerHeight * 0.62)}
              onDone={() => setTimeout(() => setChallengeWheelDoneFor(currentChallengeRound.id), 1500)}
            />
          </div>
        )}
        {currentChallengeRound?.phase === 'CHALLENGE_DRAWN' && challengeWheelDoneFor === currentChallengeRound.id && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center px-20"
            data-testid="challenge-reveal"
          >
            <motion.div
              initial={{ y: -100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-8xl mb-8"
            >
              🎲
            </motion.div>
            <motion.h1 
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-7xl font-black text-purple-400 mb-8"
              style={{ textShadow: '0 0 40px rgba(168, 85, 247, 0.5)' }}
            >
              {currentChallengeRound.challengeTitle}
            </motion.h1>
            <motion.p
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-3xl text-white/70 mb-10 max-w-4xl mx-auto"
            >
              {currentChallengeRound.challengeDescription}
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="inline-block px-8 py-4 bg-amber-500/20 border-2 border-amber-500/50 rounded-2xl"
            >
              <p className="text-xl text-amber-400 uppercase tracking-widest mb-2">SUCCESS CRITERIA</p>
              <p className="text-2xl text-white">{currentChallengeRound.successCriteria}</p>
            </motion.div>
          </motion.div>
        )}

        {/* CHALLENGER_DRAWN - Player Wheel (SpinWheel lands on the real backend winner) */}
        {currentChallengeRound?.phase === 'CHALLENGER_DRAWN' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
            data-testid="challenger-wheel-stage"
          >
            <p className="text-2xl text-purple-400 mb-4">{currentChallengeRound.challengeTitle}</p>

            {(challengerRevealFor !== currentChallengeRound.id && eligibleChallengers.length > 0) ? (
              <SpinWheel
                title="🎡 WER MUSS RAN?"
                testId="challenger-wheel"
                segments={eligibleChallengers.map(p => ({ id: p.userId, label: p.displayName }))}
                targetId={currentChallengeRound.challengerUserId}
                size={Math.min(560, window.innerHeight * 0.6)}
                onDone={() => setTimeout(() => setChallengerRevealFor(currentChallengeRound.id), 800)}
              />
            ) : (
              /* Challenger Revealed */
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
              >
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-[12rem] mb-6"
                >
                  👤
                </motion.div>
                <motion.h1 
                  className="text-8xl font-black text-amber-400"
                  style={{ textShadow: '0 0 60px rgba(251, 191, 36, 0.5)' }}
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  data-testid="challenger-name-reveal"
                >
                  {currentChallengeRound.challengerDisplayName}
                </motion.h1>
                <p className="text-3xl text-white/50 mt-6">ist der Challenger!</p>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* BETTING_OPEN - Live Betting */}
        {currentChallengeRound?.phase === 'BETTING_OPEN' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full max-w-6xl mx-auto px-10"
          >
            {/* Header */}
            <div className="text-center mb-8">
              <p className="text-2xl text-purple-400">{currentChallengeRound.challengeTitle}</p>
              <p className="text-4xl font-bold text-amber-400 mt-2">{currentChallengeRound.challengerDisplayName}</p>
            </div>
            
            {/* SCHAFFT ER DAS? */}
            <motion.h1 
              className="text-7xl font-black text-center text-white mb-12"
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              SCHAFFT ER DAS?
            </motion.h1>
            
            {/* Odds Display */}
            <div className="grid grid-cols-2 gap-10 mb-10">
              <motion.div 
                className="bg-green-900/30 border-4 border-green-500/50 rounded-3xl p-10 text-center"
                animate={{ borderColor: ['rgba(34, 197, 94, 0.5)', 'rgba(34, 197, 94, 0.8)', 'rgba(34, 197, 94, 0.5)'] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <p className="text-3xl text-green-400 uppercase tracking-widest mb-4">JA</p>
                <motion.p 
                  className="text-[8rem] font-black text-green-400 leading-none"
                  key={currentChallengeRound.yesOdds}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                >
                  x{currentChallengeRound.yesOdds?.toFixed(2)}
                </motion.p>
                <p className="text-2xl text-white/50 mt-4">{currentChallengeRound.yesStake || 0} Coins</p>
              </motion.div>
              
              <motion.div 
                className="bg-red-900/30 border-4 border-red-500/50 rounded-3xl p-10 text-center"
                animate={{ borderColor: ['rgba(239, 68, 68, 0.5)', 'rgba(239, 68, 68, 0.8)', 'rgba(239, 68, 68, 0.5)'] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <p className="text-3xl text-red-400 uppercase tracking-widest mb-4">NEIN</p>
                <motion.p 
                  className="text-[8rem] font-black text-red-400 leading-none"
                  key={currentChallengeRound.noOdds}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                >
                  x{currentChallengeRound.noOdds?.toFixed(2)}
                </motion.p>
                <p className="text-2xl text-white/50 mt-4">{currentChallengeRound.noStake || 0} Coins</p>
              </motion.div>
            </div>
            
            {/* Countdown + Bet Count */}
            <div className="flex justify-between items-center">
              <div className="text-center">
                <p className="text-xl text-white/50 uppercase">Wetten</p>
                <p className="text-5xl font-bold text-white">{challengeBets.length}</p>
              </div>
              
              {bettingCountdown !== null && (
                <motion.div 
                  className="text-center bg-amber-900/30 px-10 py-4 rounded-2xl"
                  animate={{ scale: bettingCountdown <= 10 ? [1, 1.05, 1] : 1 }}
                  transition={{ duration: 0.5, repeat: bettingCountdown <= 10 ? Infinity : 0 }}
                >
                  <p className="text-xl text-amber-400 uppercase">Zeit</p>
                  <p className={`text-6xl font-black ${bettingCountdown <= 10 ? 'text-red-500' : 'text-white'}`}>
                    {Math.floor(bettingCountdown / 60)}:{(bettingCountdown % 60).toString().padStart(2, '0')}
                  </p>
                </motion.div>
              )}
              
              <div className="text-center">
                <p className="text-xl text-white/50 uppercase">Total Pool</p>
                <p className="text-5xl font-bold text-white">
                  {(currentChallengeRound.yesStake || 0) + (currentChallengeRound.noStake || 0)}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* BETTING_LOCKED */}
        {currentChallengeRound?.phase === 'BETTING_LOCKED' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            {currentChallengeRound.secondaryPlayerNames?.length > 0 ? (
              <>
                <h1 className="text-5xl font-black text-white mb-2">{currentChallengeRound.challengeTitle}</h1>
                <p className="text-3xl text-amber-400 mb-4">Challenger: {currentChallengeRound.challengerDisplayName}</p>
                {renderSecondaryPlayers()}
              </>
            ) : (
              <>
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="text-[10rem] mb-8"
                >
                  🔒
                </motion.div>
                <h1 className="text-7xl font-black text-red-500 mb-6">WETTEN GESCHLOSSEN</h1>
                <p className="text-3xl text-white/50">{challengeBets.length} Wetten platziert</p>
                <p className="text-2xl text-purple-400 mt-6">{currentChallengeRound.challengeTitle}</p>
                <p className="text-4xl text-amber-400 mt-2">{currentChallengeRound.challengerDisplayName}</p>
              </>
            )}
          </motion.div>
        )}

        {/* IN_PROGRESS - Challenge being performed */}
        {currentChallengeRound?.phase === 'IN_PROGRESS' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center px-20"
          >
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              className={session?.challengeLive?.roundId === currentChallengeRound.id ? 'text-5xl mb-2' : 'text-[10rem] mb-8'}
            >
              ⏳
            </motion.div>
            <ChallengeLiveStage round={currentChallengeRound} live={session?.challengeLive} />
            <h1 className="text-7xl font-black text-amber-400 mb-6">
              {currentChallengeRound.challengeTitle}
            </h1>
            <p className="text-5xl font-bold text-white mb-8">
              {currentChallengeRound.challengerDisplayName}
            </p>
            <div className="inline-block px-8 py-4 bg-purple-500/20 border-2 border-purple-500/50 rounded-2xl mb-8">
              <p className="text-2xl text-white">{currentChallengeRound.successCriteria}</p>
            </div>
            
            {/* Secondary Players */}
            {currentChallengeRound.secondaryPlayerNames?.length > 0 && renderSecondaryPlayers()}
          </motion.div>
        )}

        {/* SETTLED - Result */}
        {currentChallengeRound?.phase === 'SETTLED' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="text-[14rem] mb-6"
            >
              {currentChallengeRound.result === 'SUCCESS' ? '✅' :
               currentChallengeRound.result === 'FAIL' ? '❌' : '⊘'}
            </motion.div>
            <motion.h1 
              className={`text-9xl font-black mb-6 ${
                currentChallengeRound.result === 'SUCCESS' ? 'text-green-400' :
                currentChallengeRound.result === 'FAIL' ? 'text-red-400' : 'text-gray-400'
              }`}
              style={{ textShadow: currentChallengeRound.result === 'SUCCESS' 
                ? '0 0 60px rgba(34, 197, 94, 0.5)' 
                : currentChallengeRound.result === 'FAIL'
                ? '0 0 60px rgba(239, 68, 68, 0.5)'
                : 'none'
              }}
            >
              {currentChallengeRound.result}
            </motion.h1>
            <p className="text-4xl text-white mb-4">{currentChallengeRound.challengerDisplayName}</p>
            <p className="text-2xl text-purple-400">{currentChallengeRound.challengeTitle}</p>
            
            {/* Winner info */}
            <div className="mt-10 text-2xl text-white/60">
              {currentChallengeRound.result === 'SUCCESS' && (
                <p>JA-Wetten gewinnen! Challenger bekommt Bonus!</p>
              )}
              {currentChallengeRound.result === 'FAIL' && (
                <p>NEIN-Wetten gewinnen!</p>
              )}
              {currentChallengeRound.result === 'VOID' && (
                <p>Alle Einsätze werden zurückerstattet.</p>
              )}
            </div>
          </motion.div>
        )}

        {/* ==================== SESSION COMPLETE ==================== */}
        {phase === PHASES.SESSION_COMPLETE && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ duration: 3, repeat: Infinity }}
              className="text-[10rem] mb-10"
            >
              🏆
            </motion.div>
            <motion.h1 
              className="text-8xl font-black text-white mb-8"
              style={{ textShadow: '0 0 60px rgba(245, 158, 11, 0.5)' }}
            >
              SESSION COMPLETE!
            </motion.h1>
            <p className="text-3xl text-white/50 mb-8">
              Alle Predictions haben jetzt ihre Quote!
            </p>
            <motion.p 
              className="text-2xl text-amber-400"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Jetzt geht's ans Wetten...
            </motion.p>
          </motion.div>
        )}
      </div>

      {/* RheinStars subtle branding */}
      <div className="absolute bottom-6 left-6 opacity-20">
        <span className="text-white text-lg font-bold tracking-widest">RHEINSTARS</span>
      </div>
      
      {/* User counter */}
      {session?.currentUserIndex !== undefined && session?.currentUserIndex >= 0 && (
        <div className="absolute top-6 right-6 bg-black/40 backdrop-blur-sm rounded-xl px-5 py-3">
          <span className="text-white/50 text-lg font-light">
            <span className="font-bold text-white">{(session.currentUserIndex ?? 0) + 1}</span>
            <span className="mx-2">/</span>
            <span>{session.userOrder?.length || 0}</span>
          </span>
        </div>
      )}

      {/* Audio loading indicator */}
      {!audioLoaded && currentAudioUrl && (
        <div className="absolute top-6 left-6">
          <motion.div
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="text-white/30 text-sm"
          >
            🎵 Loading audio...
          </motion.div>
        </div>
      )}
    </div>
  );
}
